import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { ToastrService } from 'ngx-toastr';
import { ForgotPasswordConstant } from '../../constants/forgot-password.constant';
import { ForgotPasswordValid } from '../../models/forgot-password-valid.model';
import { SmsResponse } from '../../models/sms-response.model';
import { User } from '../../models/user.model';
import { UserRegistrationService } from '../../services/user-registration.service';
import { Languages, SharedUrls } from '../../../shared/constants/shared.constants';
import { LayoutService } from '../../../layout/state/layout.service';
import { LayoutQuery } from '../../../layout/state/layout.query';
import { UserDetailsComponent } from "../user-details/user-details.component";
import { UserOtpComponent } from "../user-otp/user-otp.component";
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-generate-password',
  templateUrl: './generate-password.component.html',
  styleUrls: ['./generate-password.component.scss'],
  standalone: false,
})
export class GeneratePasswordComponent implements OnInit {
  identifier: string = '';
  languages = Languages;
  displayOtp = false;
  mobileNo: string = '';
  name: string = '';
  showError = false;
  showSpinner = false;
  otpWaitingInMinutes: number | undefined;
  onlinepPay = false;
  unsuccessfullAttempt = 0;
  forgotpass = false;
  maxOtpResendAttempts!: number;

  constructor(
    private router: Router,
    private activateroute: ActivatedRoute,
    private service: UserRegistrationService,
    public layoutService: LayoutService,
    public layoutQuery: LayoutQuery,
    private toastrService: ToastrService,
    private translocoService: TranslocoService
  ) { }

  ngOnInit(): void {
    const isFromForgotPassword = this.activateroute.snapshot.params['forgotpass'];
    this.forgotpass = isFromForgotPassword ? Boolean(JSON.parse(isFromForgotPassword)) : false;

    if (this.forgotpass) {
      this.handleForgotPasswordRedirect();
      return;
    }

    this.identifier = this.activateroute.snapshot.params['guid'];
    const userDetails: HttpResponse<User> = this.activateroute.snapshot.data['userDetails'];
    const user = userDetails.body;

    if (user) {
      this.onlinepPay = user.type === 1;
      const language = Languages.find(x => x.code === user.languageId) || Languages.find(x => x.code === 'en');
      this.layoutService.selectLanguage(language!);
      this.name = user.name;
    }
  }

  detailsVerify($event: { mobileNumber: string }) {
    if (!$event?.mobileNumber) return;

    this.showSpinner = true;
    this.showError = false;
    this.mobileNo = $event.mobileNumber;

    const handler$ = this.forgotpass
      ? this.service.forgotPassVerify({ mobileNumber: $event.mobileNumber }) as import("rxjs").Observable<SmsResponse>
      : this.service.verify(this.identifier, { mobileNumber: $event.mobileNumber }) as import("rxjs").Observable<SmsResponse>;

    handler$.subscribe({
      next: (response: SmsResponse) => {
        if (['pending', 'approved'].includes(response.status)) {
          this.otpWaitingInMinutes = response.otpWaitingInMinutes;
          this.displayOtp = true;
          this.identifier = response.identifier || this.identifier;
          this.maxOtpResendAttempts = response.maxOtpResendAttempts;
          this.showSpinner = true;

          if (this.forgotpass) {
            const forgotPasswordValid = this.getForgotPasswordValid();
            if (forgotPasswordValid && !forgotPasswordValid.restrictDate) {
              localStorage.removeItem(ForgotPasswordConstant.ForgotPasswordValid);
            }
          }

        } 
      },
      error: () => {
        this.setErrorSpinner();
      }
    });
  }


  otpVerify($event: { otp: string }) {
    if (!$event?.otp) return;

    this.showError = false;
    this.showSpinner = true;

    (this.service.otpVerify(this.identifier, $event.otp.toString()) as import("rxjs").Observable<SmsResponse>).subscribe({
      next: (response: SmsResponse) => {
        this.showSpinner = false;

        switch (response.status) {
          case 'MFAVerified': {
            const redirectUrl = response.type === 1
              ? `amghara/pay-confirm/${this.identifier}`
              : `user-registration/set-pwd/${this.identifier}/${this.forgotpass}`;
            this.router.navigate([redirectUrl]);
            break;
          }

          case 'pending': {
            this.showError = true;
            this.unsuccessfullAttempt++;
            break;
          }

          default: {
            this.setErrorSpinner();
            break;
          }
        }
      },
      error: () => this.setErrorSpinner()
    });
  }

  getForgotPasswordValid(): ForgotPasswordValid | null {
    const json = localStorage.getItem(ForgotPasswordConstant.ForgotPasswordValid);
    return json ? JSON.parse(json) : null;
  }

  setErrorSpinner() {
    this.showError = true;
    this.showSpinner = false;
  }

  private handleForgotPasswordRedirect() {
    const forgotPasswordValid = this.getForgotPasswordValid();
    if (forgotPasswordValid?.restrictDate && new Date(forgotPasswordValid.restrictDate) > new Date()) {
      this.router.navigate([SharedUrls.Login]);
      this.toastrService.error(this.translocoService.translate('challenge.ToManyAttempt'));
    } else {
      localStorage.removeItem(ForgotPasswordConstant.ForgotPasswordValid);
    }
  }

  private handleInvalidDetails() {
    const stored = this.getForgotPasswordValid();

    if (stored && new Date() > new Date(stored.attemptDate)) {
      localStorage.removeItem(ForgotPasswordConstant.ForgotPasswordValid);
    }

    let forgotPasswordValid = this.getForgotPasswordValid();
    if (!forgotPasswordValid) {
      const attemptDate = new Date();
      attemptDate.setMinutes(attemptDate.getMinutes() + 15);
      forgotPasswordValid = { count: 1, attemptDate, restrictDate: undefined };
    } else {
      forgotPasswordValid.count++;
      if (forgotPasswordValid.count > 3 && new Date(forgotPasswordValid.attemptDate) > new Date()) {
        if (forgotPasswordValid.count === 4) {
          const restrictDate = new Date();
          restrictDate.setMinutes(restrictDate.getMinutes() + 30);
          forgotPasswordValid.restrictDate = restrictDate;
        }
        this.router.navigate([SharedUrls.Login]);
        this.toastrService.error(this.translocoService.translate('challenge.ToManyAttempt'));
      }
    }

    localStorage.setItem(ForgotPasswordConstant.ForgotPasswordValid, JSON.stringify(forgotPasswordValid));
    this.showSpinner = false;
    this.toastrService.error(this.translocoService.translate('challenge.ForgotPasswordInvalid'));
  }
}
